import { bootstrapApplication, BrowserModule } from '@angular/platform-browser';
import { PreloadAllModules, provideRouter, RouteReuseStrategy, withPreloading } from '@angular/router';
import { IonicRouteStrategy, provideIonicAngular } from '@ionic/angular/standalone';
import { App } from './app/app';
import { appRoutes } from './app/app.routes';
import { DEFAULT_CURRENCY_CODE, ErrorHandler, importProvidersFrom, inject, LOCALE_ID, provideAppInitializer } from '@angular/core';
import { provideTranslateService } from '@ngx-translate/core';
import { provideModuleTranslateLoader } from '@larscom/ngx-translate-module-loader';
import { ApiModule } from '@work-frontend-monorepo/work-rest-api';
import { environment } from '@work-frontend-monorepo/work-environments';
import { AppInitializer } from './app/app.initializer';
import * as Sentry from '@sentry/angular';
import { DATE_PIPE_DEFAULT_OPTIONS } from '@angular/common';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { ApiTokenInterceptor, WorkAuthenticationModule } from '@work-frontend-monorepo/work-authentication';
import { WorkStateModule } from '@work-frontend-monorepo/work-ui/state-management';
import { APPLICATION_NAME } from '@work-frontend-monorepo/work-ui/utilities';
import { SelectDropdownOptionsApiService } from '@work-frontend-monorepo/work-ui/forms';
import '@angular/common/locales/global/de-CH';

// Sentry/Bugsink Reporting
Sentry.init({
  dsn: environment.sentryDsn.app,
  environment: environment.name,
  enabled: environment.name !== 'localhost',
  initialScope: {
    tags: { tenant: environment.tenantCompany },
  },
});

const initializeApp = (appInitializer: AppInitializer): Promise<void> => appInitializer.init();
const datePipeOptionsFactory = () => {
  return {
    dateFormat: 'shortDate',
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  };
};

const baseTranslateUrl = './assets/i18n';

bootstrapApplication(App, {
  providers: [
    { provide: RouteReuseStrategy, useClass: IonicRouteStrategy },
    provideIonicAngular({
      backButtonText: 'Zurück',
    }),
    provideRouter(appRoutes, withPreloading(PreloadAllModules)),
    provideTranslateService({
      loader: provideModuleTranslateLoader({
        modules: [
          { baseTranslateUrl }, // Globale Übersetzungen
          { moduleName: 'SFG-OVERRIDES', baseTranslateUrl }, // SFG-spezifische Übersetzungen
          { moduleName: 'APP', baseTranslateUrl }, // App-spezifische Übersetzungen
        ],
        disableNamespace: true,
      }),
      fallbackLang: 'de',
      lang: 'de',
    }),
    importProvidersFrom(
      ApiModule.forRoot({ rootUrl: environment.apiConfiguration.baseUrl }),
      WorkAuthenticationModule,
      WorkStateModule,
      BrowserModule,
    ),
    {
      provide: LOCALE_ID,
      useValue: 'de-CH',
    },
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useFactory: datePipeOptionsFactory,
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'CHF',
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ApiTokenInterceptor,
      multi: true,
    },
    AppInitializer,
    provideAppInitializer(() => initializeApp(inject(AppInitializer))),
    {
      provide: APPLICATION_NAME,
      useValue: environment.workOffice.appName,
    },
    SelectDropdownOptionsApiService,
    provideHttpClient(withInterceptorsFromDi()),
    provideRouter(appRoutes),
    {
      provide: ErrorHandler,
      useValue: Sentry.createErrorHandler(),
    },
  ],
});
